import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

interface AuthContextType {
  isAuthenticated: boolean;
  user: CognitoUser | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  signUp: (username: string, password: string, email: string) => Promise<void>;
  confirmEmail: (username: string, code: string) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  resetPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  signInWithGoogle: () => void;
  signInWithApple: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  signIn: async () => {},
  signOut: () => {},
  signUp: async () => {},
  confirmEmail: async () => {},
  forgotPassword: async () => {},
  resetPassword: async () => {},
  signInWithGoogle: () => {},
  signInWithApple: () => {},
});

const poolData = {
  UserPoolId: 'us-east-1_uY6gAvKBR',
  ClientId: 'dtos1299q3hfa5q2c01kfh2o0',
};

const userPool = new CognitoUserPool(poolData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<CognitoUser | null>(null);

  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          console.error('Error getting session:', err);
        } else if (session && session.isValid()) {
          setIsAuthenticated(true);
          setUser(currentUser);
        }
      });
    }
  }, []);

  const signIn = (username: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          setIsAuthenticated(true);
          setUser(cognitoUser);
          resolve();
        },
        onFailure: (err) => {
          console.error('Error during authentication:', err);
          reject(err);
        },
      });
    });
  };

  const signOut = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const signUp = async (username: string, password: string, email: string): Promise<void> => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: username }),
    ];

    try {
      const result = await new Promise<void>((resolve, reject) => {
        userPool.signUp(email, password, attributeList, [], (err, result) => {
          if (err) {
            console.error('Error during sign up:', err);
            reject(err);
          } else {
            resolve();
          }
        });
      });
    } catch (error) {
      console.error('Error during sign up:', error);
    }
  };

  const confirmEmail = (username: string, code: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          console.error('Error during email confirmation:', err);
          reject(err);
        } else {
          resolve();
        }
      });
    });
  };
  

  const forgotPassword = (username: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          console.error('Error during forgot password:', err);
          reject(err);
        },
      });
    });
  };

  const resetPassword = (username: string, code: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          console.error('Error during password reset:', err);
          reject(err);
        },
      });
    });
  };

  // Google Sign-In implementation
  const signInWithGoogle = () => {
    const googleSignInUrl = `https://${poolData.UserPoolId}.auth.us-east-1.amazoncognito.com/oauth2/authorize?identity_provider=Google&redirect_uri=${encodeURIComponent(
      '/' // replace with your redirect URI
    )}&response_type=CODE&client_id=${poolData.ClientId}&scope=email+openid+profile`;
    window.location.assign(googleSignInUrl);
  };

  // Apple Sign-In implementation
  const signInWithApple = () => {
    const appleSignInUrl = `https://${poolData.UserPoolId}.auth.us-east-1.amazoncognito.com/oauth2/authorize?identity_provider=SignInWithApple&redirect_uri=${encodeURIComponent(
      '/' // replace with your redirect URI
    )}&response_type=CODE&client_id=${poolData.ClientId}&scope=email+openid+profile`;
    window.location.assign(appleSignInUrl);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        signIn,
        signOut,
        signUp,
        confirmEmail,
        forgotPassword,
        resetPassword,
        signInWithGoogle,
        signInWithApple,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};